import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw } from 'lucide-react';
import AdminLayout from '../components/admin/AdminLayout';

const API = process.env.REACT_APP_BACKEND_URL;

/* ─────────────────────── Login ─────────────────────── */

function LoginScreen({ onLogin, error }) {
  const [pw, setPw] = useState('');
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);
  return (
    <div style={ls.wrap}>
      <div style={ls.card}>
        <div style={{ fontSize: 28, marginBottom: 8 }}>🌲</div>
        <h1 style={ls.title}>Magic Forest</h1>
        <p style={ls.sub}>Admin Console</p>
        <form onSubmit={e => { e.preventDefault(); onLogin(pw); }} style={{ width: '100%', marginTop: 24 }}>
          <input ref={inputRef} type="password" placeholder="Admin password"
            value={pw} onChange={e => setPw(e.target.value)} style={ls.input} />
          {error && <p style={ls.error}>{error}</p>}
          <button type="submit" style={ls.btn}>Sign In</button>
        </form>
      </div>
    </div>
  );
}

/* ─────────────────────── Main ─────────────────────── */

export default function AdminPanel() {
  const [password, setPassword] = useState(() => sessionStorage.getItem('admin_pw') || '');
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState('');
  const [data, setData] = useState(null);
  const [healthStatus, setHealthStatus] = useState(null);
  const [healthChecks, setHealthChecks] = useState([]);
  const [loading, setLoading] = useState(false);
  const intervalRef = useRef(null);
  const headers = { 'X-Admin-Password': password };

  const handleLogin = async (pw) => {
    setAuthError('');
    try {
      const resp = await fetch(`${API}/api/admin/auth`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: pw }),
      });
      if (resp.status === 401) { setAuthError('Invalid password'); return; }
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setPassword(pw); sessionStorage.setItem('admin_pw', pw); setAuthed(true);
    } catch { setAuthError('Connection failed'); }
  };

  useEffect(() => { if (password && !authed) handleLogin(password); }, []);

  const fetchAll = useCallback(async () => {
    if (!password) return;
    setLoading(true);
    try {
      const [st, hs, ch] = await Promise.all([
        fetch(`${API}/api/admin/status`, { headers }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/api/admin/health/status`, { headers }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/api/admin/health/checks?limit=60`, { headers }).then(r => r.ok ? r.json() : null),
      ]);
      if (st) setData(st);
      if (hs) setHealthStatus(hs);
      if (ch) setHealthChecks(ch.checks || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [password]);

  useEffect(() => { if (authed) fetchAll(); }, [authed, fetchAll]);
  useEffect(() => {
    if (!authed) return;
    intervalRef.current = setInterval(fetchAll, 30000);
    return () => clearInterval(intervalRef.current);
  }, [authed, fetchAll]);

  if (!authed) return <LoginScreen onLogin={handleLogin} error={authError} />;

  const nodes = data?.nodes || [];
  const mode = healthStatus?.execution_mode;
  const overallHealthy = healthStatus?.overall_status === 'healthy';

  return (
    <AdminLayout activePage="overview" onLogout={() => { sessionStorage.removeItem('admin_pw'); setAuthed(false); }}>
      {/* Status + Refresh */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 8, height: 8, borderRadius: '50%',
            backgroundColor: overallHealthy ? '#4ade80' : '#fbbf24',
            boxShadow: `0 0 8px ${overallHealthy ? '#4ade80' : '#fbbf24'}40`,
          }} />
          <span style={{ fontSize: 14, fontWeight: 600, color: overallHealthy ? '#4ade80' : '#fbbf24' }}>
            {overallHealthy ? 'All Systems Operational' : healthStatus?.overall_status || 'Checking...'}
          </span>
        </div>
        <button onClick={fetchAll} disabled={loading} style={p.refreshBtn}>
          <RefreshCw size={13} /> {loading ? 'Checking...' : 'Refresh'}
        </button>
      </div>

      {/* Stats */}
      <div style={p.statsGrid}>
        <StatCard label="Services" value={`${nodes.filter(n => n.status === 'ok').length}/${nodes.length}`} color="#4ade80" />
        <StatCard label="Mode" value={mode?.mode?.replace('_', ' ') || '—'} color={mode?.mode === 'worker_primary' ? '#4ade80' : '#f87171'} />
        <StatCard label="Incidents" value={healthStatus?.open_incidents?.length || 0} color={healthStatus?.open_incidents?.length > 0 ? '#fbbf24' : '#4ade80'} />
        <StatCard label="Worker Latency" value={healthStatus?.workers?.[0]?.response_time_ms ? `${healthStatus.workers[0].response_time_ms}ms` : '—'} color="#60a5fa" />
      </div>

      {/* Service Fleet */}
      <h2 style={p.sectionTitle}>Service Fleet</h2>
      <div style={p.serviceGrid}>
        {nodes.map(node => (
          <ServiceCard key={node.id} node={node} healthChecks={healthChecks} />
        ))}
        {nodes.length === 0 && <div style={p.empty}>Waiting for service data...</div>}
      </div>
    </AdminLayout>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div style={p.statCard}>
      <div style={{ fontSize: 10, color: '#3a3a3a', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color, fontFamily: 'monospace', marginTop: 4 }}>{value}</div>
    </div>
  );
}

function ServiceCard({ node, healthChecks }) {
  const ok = node.status === 'ok';
  const color = ok ? '#4ade80' : '#f87171';
  const isWorker = node.id?.includes('worker');
  const wChecks = isWorker ? healthChecks.slice(0, 30) : [];

  return (
    <div style={{ ...p.serviceCard, borderColor: ok ? '#1a2a1a' : '#2a1a1a' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 15 }}>{node.icon || '🔧'}</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#e5e5e5' }}>{node.name || node.id}</span>
        </div>
        <span style={{
          padding: '2px 9px', borderRadius: 10, fontSize: 10, fontWeight: 600,
          backgroundColor: ok ? '#0a1a0a' : '#1a0a0a', color,
          border: `1px solid ${ok ? '#1a3a1a' : '#3a1a1a'}`,
        }}>
          {ok ? 'Online' : 'Down'}
        </span>
      </div>
      {node.detail && <div style={{ fontSize: 10, color: '#3a3a3a', fontFamily: 'monospace', marginBottom: 6 }}>{node.detail}</div>}
      {node.metrics && (
        <div style={{ display: 'flex', gap: 20, marginTop: 6 }}>
          {Object.entries(node.metrics).slice(0, 3).map(([k, v]) => (
            <div key={k}>
              <div style={{ fontSize: 9, color: '#3a3a3a', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{k}</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#d4d4d4', fontFamily: 'monospace' }}>{v}</div>
            </div>
          ))}
        </div>
      )}
      {wChecks.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: 'flex', gap: 1.5 }}>
            {[...wChecks].reverse().map((c, i) => (
              <div key={i} style={{ flex: 1, height: 14, borderRadius: 2, minWidth: 3, backgroundColor: c.is_healthy ? '#0f3d0f' : '#3d0f0f' }} />
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3 }}>
            <span style={{ fontSize: 8, color: '#2a2a2a' }}>older</span>
            <span style={{ fontSize: 8, color: '#2a2a2a' }}>now</span>
          </div>
        </div>
      )}
    </div>
  );
}

const p = {
  refreshBtn: {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '7px 14px', borderRadius: 6, border: '1px solid #1f1f1f',
    backgroundColor: '#141414', color: '#737373', cursor: 'pointer', fontSize: 12, fontWeight: 500,
    fontFamily: 'Inter, system-ui, sans-serif',
  },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 32 },
  statCard: { padding: '16px 18px', borderRadius: 8, backgroundColor: '#0f0f0f', border: '1px solid #1a1a1a' },
  sectionTitle: {
    fontSize: 12, fontWeight: 600, color: '#525252', textTransform: 'uppercase',
    letterSpacing: '0.06em', marginBottom: 14, marginTop: 0,
  },
  serviceGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 10 },
  serviceCard: { padding: 16, borderRadius: 8, backgroundColor: '#0f0f0f', border: '1px solid #1a1a1a' },
  empty: { padding: 28, textAlign: 'center', color: '#3a3a3a', fontSize: 13 },
};

const ls = {
  wrap: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0a0a0a' },
  card: { padding: 40, borderRadius: 12, width: 340, textAlign: 'center', backgroundColor: '#0f0f0f', border: '1px solid #1a1a1a' },
  title: { margin: 0, fontSize: 20, fontWeight: 700, color: '#fafafa' },
  sub: { margin: '4px 0 0', fontSize: 12, color: '#525252' },
  input: { width: '100%', padding: '11px 14px', borderRadius: 6, border: '1px solid #1f1f1f', backgroundColor: '#0a0a0a', color: '#e5e5e5', fontSize: 13, outline: 'none', boxSizing: 'border-box', marginBottom: 12, fontFamily: 'Inter, system-ui, sans-serif' },
  btn: { width: '100%', padding: '11px 16px', borderRadius: 6, border: 'none', backgroundColor: '#4ade80', color: '#0a0a0a', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  error: { color: '#f87171', fontSize: 12, margin: '8px 0' },
};
